import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import WxCopilotHost from 'components/WxCopilotHost';
import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import {
  dispatchWorkflowInvalidated,
  processApplicantsToolResult,
  shouldInvalidate,
} from 'utils/appEvents';

const TOOL_NAMES = {
  APPLICANTS: 'postToolsApplicants',
  WORKFLOW_STAGES: 'postToolsWorkflowEditorStages',
};

/**
 * WxCueRootHost mounts the persistent Cue panel surface (`mode: 'panel-root'`)
 * ONCE at the top of the dent app, outside the routed Switch. It owns all
 * the InternalBridge wiring (tool/panel events, ephemeral context) and lives
 * for the lifetime of the page — navigation no longer remounts it, which is
 * the bug HRAI-1928 exists to fix.
 *
 * Triggers (the nav-bar Cue icon, any in-page launchers) mount separately
 * with `mode: 'trigger'` and toggle this surface via the shared Zustand
 * store inside the UMD bundle.
 */
function WxCueRootHost() {
  const whoami = useSelector(makeSelectWhoami());
  const accountWxExternalId = whoami?.account_wx_external_id;
  const isCopilotEnabled = Boolean(
    whoami?.feature_flags?.['wx-copilot-enabled'],
  );

  const handleToolEvent = useCallback(event => {
    if (
      event.type === 'tool_execution_complete' &&
      shouldInvalidate(event.result)
    ) {
      switch (event.toolName) {
        case TOOL_NAMES.APPLICANTS:
          processApplicantsToolResult(event.result);
          break;
        case TOOL_NAMES.WORKFLOW_STAGES:
          dispatchWorkflowInvalidated();
          break;
        default:
          break;
      }
    }
  }, []);

  // Build initial ephemeral context ONCE at mount. Subsequent page-context
  // updates flow through useWxCopilotPageContext inside WxCopilotHost.
  const [initialEphemeralContext] = useState(() => {
    if (typeof window === 'undefined') return undefined;
    const url = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    return JSON.stringify({ page: { url, timestamp: Date.now() } });
  });

  if (!isCopilotEnabled || !accountWxExternalId) {
    return null;
  }

  return (
    <WxCopilotHost
      mode="panel-root"
      companyUuid={accountWxExternalId}
      product="hire"
      panelClassName="fixed top-0 right-0 h-full z-50"
      locale={whoami?.wx_navbar_config?.locale}
      onToolEvent={handleToolEvent}
      ephemeralContext={initialEphemeralContext}
    />
  );
}

export default React.memo(WxCueRootHost);
